import { useState } from "react";
import type { JourneyReport } from "../lib/api.ts";
import { JourneyExplorer } from "./journeyExplorer.tsx";
import { money, num, pct, pctValue } from "../lib/format.ts";
import { Card, Kpi } from "./widgets.tsx";
import { Th, useSortable } from "./sortable.tsx";
import { dbLabel, plural, t, type L } from "../i18n/lang.ts";
import { CHANNEL, JOURNEY_STEP, SENTINEL, UI } from "../i18n/dict.ts";

/**
 * Customer journey.
 *
 * Единица здесь не лид, а **цикл одного человека** — от первого обращения до
 * состоявшегося банкета. Так просит КП: «visualization of all customer
 * touchpoints from first contact to purchase … returning customer flag …
 * journey restarts after each completed cycle».
 *
 * Экран говорит о своих границах сам, а не в сноске: человек опознаётся по
 * телефону (полной склейки ещё нет), кампания бывает измеренной и выведенной, и
 * у большинства обращений источник невосстановим. Отчёт, умалчивающий об этом,
 * читался бы как «реклама не приводит людей».
 *
 * Экран двуязычный, как и весь интерфейс. Не переводятся названия каналов,
 * стадий, парков и кампаний: они совпадают с порталом Битрикса, откуда пришли
 * данные, и перевод разорвал бы это соответствие.
 */

/**
 * Чем кончился разбор переписки. Ключ — `confidence` из ответа.
 *
 * Словарь лежит здесь, а не в общем: эти девять исходов печатает только этот
 * экран. Английская подпись из ответа остаётся запасным вариантом на случай
 * нового исхода, которого ещё нет в словаре.
 */
const COVERAGE_LABEL = {
  unique: { ru: "объявление определено однозначно", en: "ad identified unambiguously" },
  delivered: {
    ru: "определено по тому, что показывалось в тот день",
    en: "resolved by what was running that day",
  },
  ambiguous: {
    ru: "несколько кампаний — оставлено без источника",
    en: "several campaigns — left unattributed",
  },
  organic: { ru: "пост найден, рекламы за ним нет", en: "post found, no ad behind it" },
  unresolved: { ru: "ссылка есть, развернуть не удалось", en: "link present, could not be resolved" },
  nolink: { ru: "в первом сообщении ссылки нет", en: "no link in the first message" },
  outside: { ru: "вне окна данных о показах", en: "outside the delivery-data window" },
  conflict: { ru: "объявление принадлежит другому бренду", en: "ad belongs to another brand" },
  starter: {
    ru: "стартовый текст кнопки WhatsApp из объявлений Google",
    en: "starter text of the WhatsApp button in Google ads",
  },
  site_ref: {
    ru: "код сайта бренда «Ref:» в первом сообщении",
    en: "brand website code “Ref:” in the first message",
  },
} satisfies Record<string, L>;

/** Проза этого экрана. Один блок в начале файла — см. App.tsx. */
const TX = {
  /* --- два вида на одном экране */
  journeyViewAria: { ru: "Вид пути клиента", en: "Journey view" },
  tabHowPeopleMove: { ru: "Как ходят люди", en: "How people move" },
  tabClientLookup: { ru: "Поиск клиента", en: "Client lookup" },

  /* --- плитки */
  people: { ru: "Люди", en: "People" },
  /* Счётное существительное: в русском три формы. */
  cyclesCount: {
    one: { ru: "цикл", en: "cycle" },
    few: { ru: "цикла", en: "cycles" },
    many: { ru: "циклов", en: "cycles" },
  },
  touchesOnAverage: { ru: "касания в среднем", en: "touches on average" },
  returning: { ru: "Вернулись", en: "Returning" },
  ofCyclesCameBack: {
    ru: "циклов — вернулись после завершённого",
    en: "of cycles — came back after a completed one",
  },
  reachedBanquet: { ru: "Дошли до банкета", en: "Reached a banquet" },
  ofCycles: { ru: "циклов", en: "of cycles" },
  sourceKnown: { ru: "Источник известен", en: "Source known" },
  cyclesCarryCampaign: {
    one: { ru: "цикл с кампанией", en: "cycle carries a campaign" },
    few: { ru: "цикла с кампанией", en: "cycles carry a campaign" },
    many: { ru: "циклов с кампанией", en: "cycles carry a campaign" },
  },

  /* --- воронка касаний */
  touchFunnel: { ru: "Воронка касаний", en: "Touch funnel" },
  cyclesNotLeads: { ru: "циклы, а не лиды", en: "cycles, not leads" },
  stepsAre: { ru: "Ступени", en: "The steps are" },
  notStrictlyNested: { ru: "не вложены строго", en: "not strictly nested" },
  nestingWhy: {
    ru: ": сделка может появиться, хотя лид так и не стал квалифицированным, и",
    en: ": a deal can appear without the lead ever becoming qualified, and",
  },
  /* Цифры в этой оговорке замерены один раз и вписаны руками — как и в
     английском оригинале. Меняются только разделители разрядов и дробной части. */
  nestingMeasured: {
    ru: "2 730 из 2 892 циклов со сделкой (94,4 %)",
    en: "2,730 of 2,892 cycles with a deal (94.4%)",
  },
  nestingConclusion: {
    ru: "прошли именно так. Поэтому каждая ступень — доля от всей когорты, а не от ступени выше.",
    en: "did exactly that. Each step is therefore a share of the whole cohort, not of the step above it.",
  },

  /* --- чем привели */
  whatBroughtThemIn: { ru: "Чем привели", en: "What brought them in" },
  firstTouchOfCycle: { ru: "первое касание цикла", en: "first touch of the cycle" },
  colChannel: { ru: "Канал", en: "Channel" },
  colCycles: { ru: "Циклы", en: "Cycles" },
  colBanquets: { ru: "Банкеты", en: "Banquets" },
  colRate: { ru: "Конверсия", en: "Rate" },
  colRevenueAed: { ru: "Выручка, AED", en: "Revenue, AED" },

  /* --- кампании на первом касании */
  campaignsOnFirstTouch: { ru: "Кампании на первом касании", en: "Campaigns on first touch" },
  measuredInferredApart: {
    ru: "измеренное и выведенное держим отдельно",
    en: "measured and inferred kept apart",
  },
  colHowWeKnow: { ru: "Откуда знаем", en: "How we know" },
  noKnownCampaign: {
    ru: "Ни у одного цикла за этот период нет известной кампании",
    en: "No cycle in this period carries a known campaign",
  },
  badgeMeasured: { ru: "измерено", en: "measured" },
  badgeInferred: { ru: "выведено", en: "inferred" },
  noteMeasured: { ru: "Измерено", en: "Measured" },
  measuredMeans: {
    ru: "— лид сам принёс идентификатор кампании, в параметре UTM.",
    en: "— the lead carried the campaign id itself, in a UTM parameter.",
  },
  noteInferred: { ru: "Выведено", en: "Inferred" },
  inferredMeans: {
    ru: "— кампанию восстановили по ссылке из первого сообщения. Показ в день сообщения проверяется только там, где одна ссылка подошла нескольким кампаниям; там, где подошла ровно одна, эта однозначность и есть ответ, и подтверждение показа не требуется. Второй столбец слабее первого, поэтому он помечен, а не слит с первым в один.",
    en: "— the campaign was recovered from the link in the first message. Delivery on the day of the message is checked only where one link matched several campaigns; where it matched exactly one, that uniqueness is the answer and delivery is not required. The second column is weaker than the first, so it is labelled rather than merged into one.",
  },

  /* --- пришли одним путём, сконвертировались другим */
  arrivedOneWay: {
    ru: "Пришли одним путём, сконвертировались другим",
    en: "Arrived one way, converted another",
  },
  divergencesOnly: { ru: "только расхождения", en: "divergences only" },
  colFirstTouch: { ru: "Первое касание", en: "First touch" },
  colLastTouch: { ru: "Последнее касание", en: "Last touch" },
  noDivergence: {
    ru: "Расхождений нет: люди конвертируются через тот же канал, которым пришли",
    en: "No divergence: people convert through the channel they arrived on",
  },

  /* --- границы разбора переписок */
  whatChatsCouldTell: { ru: "Что удалось понять из переписок", en: "What the chats could tell us" },
  limitsStated: { ru: "границы названы, а не спрятаны", en: "the limits, stated rather than hidden" },
  colOutcome: { ru: "Итог", en: "Outcome" },
  identifiedBy: { ru: "Человек опознаётся", en: "A person is identified" },
  byPhoneNumber: { ru: "по номеру телефона", en: "by phone number" },
  identityStitching: {
    ru: ": полной склейки личности пока нет, поэтому один человек с двумя номерами выглядит как два человека, а лид без номера телефона не соединяется ни с кем. Официальная метка Meta (",
    en: ": full identity stitching does not exist yet, so one person using two numbers looks like two people, and a lead with no phone number never joins anyone. Meta's official tag (",
  },
  ctwaUnavailable: {
    ru: ") недоступна — она работает только на номерах, подключённых через WhatsApp Business API, а эти живут на обычном QR-канале Wazzup.",
    en: ") is unavailable — it only works on numbers connected through the WhatsApp Business API, and these run on Wazzup's ordinary QR channel.",
  },

  /* ------------------------------------------------------------------------
   * «Этот человек ранее обращался с рекламы».
   *
   * КАЖДОЕ СЛОВО ЗДЕСЬ ВЫВЕРЕНО, и вот против чего. Заказчик просил находить
   * тех, кто «обратился под влиянием рекламы». Соблазн подписать блок «источник
   * — реклама» огромен, и это была бы ложь: о показе рекламы мы не знаем
   * НИЧЕГО, и утверждение здесь совсем другое — про ПРЕЖНЕЕ ОБРАЩЕНИЕ. Стоит
   * подписи съехать к «реклама привела», и блок начнут складывать с источником,
   * а оговорка про цену лида перестанет быть проверяемой.
   *
   * Поэтому подпись обязана делать четыре вещи, и делает: говорить о прежнем
   * ОБРАЩЕНИИ, а не об источнике этого; прямо отрицать атрибуцию сделки к
   * кампании; называть разрыв во времени числом; называть границу — что о
   * показе рекламы не известно ничего.
   * --------------------------------------------------------------------- */
  priorPaidTitle: {
    ru: "Этот человек ранее обращался с рекламы",
    en: "This person inquired from an ad before",
  },
  priorPaidHint: {
    ru: "прежнее обращение, а не источник этого",
    en: "a previous inquiry, not the source of this one",
  },
  priorPaidKpi: { ru: "Таких обращений", en: "Inquiries like this" },
  priorPaidKpiFoot: {
    ru: "обращений без своего источника, у которых у ТОГО ЖЕ номера телефона было прежнее обращение с платной рекламы",
    en: "inquiries with no source of their own whose SAME phone number had an earlier inquiry from paid advertising",
  },
  priorPaidGapKpi: { ru: "Разрыв", en: "Time gap" },
  priorPaidGapFoot: {
    ru: "медиана разрыва между прежним обращением с рекламы и этим; учитываются разрывы до",
    en: "median gap between the earlier ad inquiry and this one; gaps counted up to",
  },
  priorPaidDays: { ru: "дней", en: "days" },
  priorPaidBeyondKpi: { ru: "За окном", en: "Beyond the window" },
  priorPaidBeyondFoot: {
    ru: "случаев с разрывом больше порога — они НЕ в числе слева: «видел рекламу год назад» это совпадение, а не влияние",
    en: "cases with a gap beyond the threshold — they are NOT in the number on the left: “saw an ad a year ago” is coincidence, not influence",
  },
  priorPaidWonKpi: { ru: "Дошли до банкета", en: "Reached a banquet" },
  priorPaidWonFoot: {
    ru: "из них состоявшихся банкетов, на",
    en: "of them became a banquet that happened, worth",
  },

  /* Главное отрицание. Оно стоит В БЛОКЕ, а не в документации. */
  priorPaidNotAttributionLead: {
    ru: "Это НЕ атрибуция сделки к кампании и не источник обращения.",
    en: "This is NOT attribution of a deal to a campaign, and not the source of the inquiry.",
  },
  priorPaidNotAttribution: {
    ru: "Сказано ровно одно: у этого номера телефона было прежнее обращение, и у ТОГО обращения стояла метка платной рекламы. Видел ли человек рекламу перед ЭТИМ обращением — неизвестно.",
    en: "It says exactly one thing: this phone number had an earlier inquiry, and THAT inquiry carried a paid-advertising tag. Whether the person saw an ad before THIS inquiry is unknown.",
  },
  /* Имена систем внутри фразы, а не рядом с ней: разорви её на три узла — и
     проверка двуязычия увидит непереведённый текст в разметке, а читатель
     потеряет отрицание из середины предложения. */
  priorPaidNoImpressions: {
    ru: "О показах рекламы мы не знаем ничего: ни Meta, ни Google не отвечают на вопрос «видел ли человек с этим номером наше объявление» — проверено 26 пробными запросами, закрыты все шесть путей. Поэтому эти обращения не добавляются к источнику, не входят в цену лида и не приписываются кампании: источник у них остаётся неизвестным, а число слева живёт отдельно, и складывать его с источником нельзя.",
    en: "We know nothing about ad impressions: neither Meta nor Google answers the question “did the person on this number see our ad” — 26 probe requests, all six routes closed. These inquiries are therefore not added to the source, do not enter cost per lead and are not credited to a campaign: their source stays unknown, the number on the left lives apart, and it must not be added to the source.",
  },
  priorPaidStitching: {
    ru: "Склейка идёт по номеру телефона, и номера с ненормально большим числом лидов из неё исключены — иначе номер парка или агентства приписал бы «прежнюю рекламу» десяткам посторонних людей сразу. Замер: 62 номера с более чем 12 лидами и 926 номеров короче 10 цифр; половина этого сигнала на выигранных сделках без отсечки была шумом общих номеров. И порог разрыва, и отсечка — настройки, а не константы в коде.",
    en: "People are stitched by phone number, and numbers carrying an abnormal count of leads are excluded — otherwise a park's or an agency's number would credit “an earlier ad” to dozens of unrelated people at once. Measured: 62 numbers with more than 12 leads, and 926 numbers shorter than 10 digits; without the cutoff, half of this signal on won deals was noise from shared numbers. Both the gap threshold and the cutoff are settings, not constants in the code.",
  },

  /* --- распределение разрыва */
  priorPaidGapSpread: { ru: "Разрыв во времени", en: "The time gap" },
  priorPaidGapSpreadHint: {
    ru: "распределением, а не средним",
    en: "as a distribution, not an average",
  },
  gapLe7: { ru: "до недели", en: "up to a week" },
  gapLe30: { ru: "до месяца", en: "up to a month" },
  gapLe90: { ru: "до трёх месяцев", en: "up to three months" },
  gapLe365: { ru: "до года", en: "up to a year" },
  gapGt365: { ru: "больше года", en: "over a year" },
  priorPaidGapNote: {
    ru: "Порог в 90 дней взят не на глаз: у 221 кампании с расходом наблюдаемая жизнь — медиана 8 дней, p90 45. Кампания живёт недели, и 90 дней это двойной p90 — самое щедрое окно, в котором объявление ещё могло стоять перед человеком.",
    en: "The 90-day threshold is not a guess: across 221 campaigns with spend the observed lifetime is a median of 8 days and a p90 of 45. A campaign lives for weeks, and 90 days is twice that p90 — the most generous window in which the ad could still have been in front of the person.",
  },

  /* --- детализация */
  priorPaidCases: { ru: "Из чего сложилось число", en: "What the number is made of" },
  priorPaidCasesHint: { ru: "по одному обращению в строке", en: "one inquiry per row" },
  priorPaidShowBeyond: { ru: "Показать и те, что за окном", en: "Show the ones beyond the window" },
  priorPaidHideBeyond: { ru: "Скрыть те, что за окном", en: "Hide the ones beyond the window" },
  priorPaidNoCases: {
    ru: "За этот период таких обращений нет",
    en: "No inquiries like this in this period",
  },
  colEnquiry: { ru: "Обращение", en: "Inquiry" },
  colEnquiredOn: { ru: "Обратился", en: "Inquired" },
  colPriorOn: { ru: "Прежнее обращение", en: "Earlier inquiry" },
  colPlatform: { ru: "Платформа", en: "Platform" },
  colPriorCampaign: { ru: "Кампания прежнего обращения", en: "Campaign of the earlier inquiry" },
  colPriorBasis: { ru: "Метка прежнего", en: "Tag on the earlier one" },
  colGapDays: { ru: "Разрыв, дней", en: "Gap, days" },
  colBanquet: { ru: "Банкет", en: "Banquet" },
  beyondWindowRow: { ru: "за окном", en: "beyond the window" },
  priorCampaignUnknown: {
    ru: "кампания не определена",
    en: "campaign not identified",
  },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

/**
 * Откуда взялась метка ПРЕЖНЕГО обращения.
 *
 * Тот же механизм `basis`, что и в списке лидов, и по той же причине: у части
 * прежних обращений платную метку поставили МЫ, разобрав ссылку из чата. Это
 * законный вывод, но не измерение заказчика. Утверждение «он раньше обращался с
 * рекламы» ровно настолько же слабее там, где слабее сама метка, — и подписано
 * это в каждой строке, а не в сноске.
 */
const PRIOR_BASIS_LABEL: Record<string, L> = {
  client_tag: { ru: "метка клиента", en: "client tag" },
  analytics_tag: { ru: "наша метка", en: "our tag" },
  chat_link: { ru: "ссылка из чата", en: "chat link" },
  site_ref: { ru: "код сайта бренда", en: "brand website code" },
};

/** Подписи ступеней разрыва. Ключ — `bucket_key` из ответа. */
const GAP_BUCKET_LABEL: Record<string, L> = {
  le_7: TX.gapLe7,
  le_30: TX.gapLe30,
  le_90: TX.gapLe90,
  le_365: TX.gapLe365,
  gt_365: TX.gapGt365,
};

/**
 * Блок «этот человек ранее обращался с рекламы».
 *
 * ТИП ОПИСАН ЗДЕСЬ, А НЕ В `lib/api.ts`, потому что тот файл не мой. Поле
 * объявлено необязательным, и блок не рисуется, когда сервер его не отдал:
 * так интерфейс не падает на ответе старого сервера. Точная правка, которую
 * нужно внести в `JourneyReport`, названа в отчёте.
 */
export type PriorPaidBlock = {
  kpi: {
    cases: number;
    cases_beyond_window: number;
    window_days: number;
    prior_leads: number;
    campaigns: number;
    prior_client_tag: number;
    prior_our_tag: number;
    median_gap_days: number | null;
  } | null;
  gapBuckets: Array<{ bucket_order: number; bucket_key: string; cases: number }>;
  cases: Array<{
    lead_id: string;
    lead_title: string | null;
    enquiry_on: string;
    park_key: string | null;
    prior_lead_id: string;
    prior_on: string;
    prior_platform: string | null;
    prior_basis: string;
    prior_campaign_name: string | null;
    gap_days: number;
    within_window: boolean;
    won: boolean;
    revenue_aed_minor: string;
  }>;
  wonDeals: number;
  wonRevenueAedMinor: string;
  casesShown: number;
  casesCap: number;
};

/**
 * Два вида на одном экране, а не два экрана.
 *
 * «Как ходят люди вообще» и «как прошёл этот человек» — разные вопросы, но один
 * и тот же доступ и одна и та же тема. Разносить их по разделам значило бы
 * заставить руководителя помнить, что путь клиента живёт в двух местах.
 */
export function JourneyScreen({
  report,
  canLookup,
}: {
  report: JourneyReport & { priorPaid?: PriorPaidBlock };
  /**
   * Право на личные данные отдельное. Скрытая вкладка — не защита (сервер
   * проверяет своё), но показывать кнопку, которая вернёт 403, нечестно.
   */
  canLookup: boolean;
}): React.ReactElement {
  const [view, setView] = useState<"overview" | "lookup">("overview");
  if (!canLookup) return <JourneyOverview report={report} />;

  return (
    <>
      <div role="tablist" aria-label={t(TX.journeyViewAria)} className="seg" style={{ marginBottom: 14 }}>
        <button
          type="button"
          role="tab"
          aria-selected={view === "overview"}
          onClick={() => setView("overview")}
        >
          {t(TX.tabHowPeopleMove)}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "lookup"}
          onClick={() => setView("lookup")}
        >
          {t(TX.tabClientLookup)}
        </button>
      </div>
      {view === "lookup" ? <JourneyExplorer /> : <JourneyOverview report={report} />}
    </>
  );
}

function JourneyOverview({
  report,
}: {
  report: JourneyReport & { priorPaid?: PriorPaidBlock };
}): React.ReactElement {
  const k = report.kpi;
  const cycles = Number(k.cycles);
  const top = report.funnel[0]?.cycles ?? 0;
  const coverageTotal = report.coverage.reduce((n, r) => n + r.leads, 0);

  /*
   * ТРИ ТАБЛИЦЫ ЭТОГО ЭКРАНА ПОКАЗЫВАЮТ ЧАСТЬ ВЫБОРКИ, и сортировать их на
   * клиенте нельзя. Сервер отдаёт их срезом без «показать ещё»:
   * `api/journey.ts:85` — `LIMIT 12` при 18 каналах в `core.dim_channel` плюс
   * «(not set)»; `:108` — `LIMIT 10` при том, что пар «первое → последнее
   * касание» может быть до 18×18; `:121` — `LIMIT 15` при 267 кампаниях в базе.
   * Клиентская сортировка переставила бы только срез, а верхнюю строку человек
   * прочитает как максимум по всей выборке — то есть отчёт назовёт неверную
   * цифру. Серверной сортировки у этих запросов нет, поэтому таблицы помечены
   * `partial`: заголовки не кликаются и подсказкой объясняют причину.
   */
  const chan = useSortable(
    report.byFirstChannel,
    {
      channel: {
        kind: "text",
        get: (r) => dbLabel(CHANNEL, r.channel_key, dbLabel(SENTINEL, r.channel_name, r.channel_name)),
      },
      cycles: { kind: "number", get: (r) => r.cycles },
      won: { kind: "number", get: (r) => r.won },
      /* Готовая доля из ответа: у канала без циклов её нет, и ячейка печатает
         прочерк — значит `null`, а не ноль. */
      winPct: { kind: "number", get: (r) => r.win_pct },
      revenue: { kind: "number", get: (r) => r.revenue_aed_minor },
    },
    { partial: true },
  );

  const camp = useSortable(
    report.topCampaigns,
    {
      campaign: { kind: "text", get: (r) => r.campaign_name },
      source: { kind: "text", get: (r) => (r.source === "utm" ? t(TX.badgeMeasured) : t(TX.badgeInferred)) },
      cycles: { kind: "number", get: (r) => r.cycles },
      won: { kind: "number", get: (r) => r.won },
      revenue: { kind: "number", get: (r) => r.revenue_aed_minor },
    },
    { partial: true },
  );

  const path = useSortable(
    report.conversionPath,
    {
      first: {
        kind: "text",
        get: (r) => dbLabel(CHANNEL, r.first_channel_key, dbLabel(SENTINEL, r.first_channel, r.first_channel)),
      },
      last: {
        kind: "text",
        get: (r) => dbLabel(CHANNEL, r.last_channel_key, dbLabel(SENTINEL, r.last_channel, r.last_channel)),
      },
      cycles: { kind: "number", get: (r) => r.cycles },
      won: { kind: "number", get: (r) => r.won },
    },
    { partial: true },
  );

  /* Границы разбора переписок приходят без `LIMIT` (`api/journey.ts:134`) —
     таблица полная, и сортировать её можно честно. */
  const cov = useSortable(report.coverage, {
    outcome: { kind: "text", get: (r) => dbLabel(COVERAGE_LABEL, r.confidence, r.confidence) },
    leads: { kind: "number", get: (r) => r.leads },
    /* Доля считается в ячейке, поэтому доступ повторяет ТУ ЖЕ формулу. При
       нулевом знаменателе `pct()` печатает прочерк — здесь это `null`. */
    share: { kind: "number", get: (r) => (coverageTotal > 0 ? r.leads / coverageTotal : null) },
  });

  return (
    <>
      <div className="grid cols-4" style={{ marginBottom: 14 }}>
        <Kpi
          label={t(TX.people)}
          value={num(Number(k.people))}
          foot={`${num(cycles)} ${plural(cycles, TX.cyclesCount)} · ${k.avg_touches ?? "—"} ${t(TX.touchesOnAverage)}`}
        />
        <Kpi
          label={t(TX.returning)}
          value={num(Number(k.repeat_cycles))}
          foot={`${pct(Number(k.repeat_cycles), cycles)} ${t(TX.ofCyclesCameBack)}`}
        />
        <Kpi
          label={t(TX.reachedBanquet)}
          value={num(Number(k.won_cycles))}
          foot={`${pct(Number(k.won_cycles), cycles)} ${t(TX.ofCycles)}`}
        />
        <Kpi
          label={t(TX.sourceKnown)}
          value={pct(Number(k.with_campaign), cycles)}
          foot={`${num(Number(k.with_campaign))} ${plural(Number(k.with_campaign), TX.cyclesCarryCampaign)}`}
        />
      </div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        <Card title={t(TX.touchFunnel)} hint={t(TX.cyclesNotLeads)}>
          {report.funnel.map((f) => (
            <div className="funnel-row" key={f.step_order}>
              <span>{dbLabel(JOURNEY_STEP, f.step_key, f.step_name)}</span>
              <div className="bar">
                <i style={{ width: `${top > 0 ? (100 * f.cycles) / top : 0}%` }} />
              </div>
              <span className="num">
                {num(f.cycles)} <small>{pct(f.cycles, top)}</small>
              </span>
            </div>
          ))}
          <div className="note" style={{ marginTop: 12 }}>
            {t(TX.stepsAre)} <b>{t(TX.notStrictlyNested)}</b>
            {t(TX.nestingWhy)} <b>{t(TX.nestingMeasured)}</b> {t(TX.nestingConclusion)}
          </div>
        </Card>

        <Card title={t(TX.whatBroughtThemIn)} hint={t(TX.firstTouchOfCycle)}>
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={chan} col="channel">{t(TX.colChannel)}</Th>
                  <Th sort={chan} col="cycles" num>{t(TX.colCycles)}</Th>
                  <Th sort={chan} col="won" num>{t(TX.colBanquets)}</Th>
                  <Th sort={chan} col="winPct" num>{t(TX.colRate)}</Th>
                  <Th sort={chan} col="revenue" num>{t(TX.colRevenueAed)}</Th>
                </tr>
              </thead>
              <tbody>
                {chan.rows.map((r) => (
                  <tr key={r.channel_name}>
                    {/* Название канала приходит подписью без ключа: переводится
                        только то, чем сервер обозначает «не сопоставлено». */}
                    <td>{dbLabel(CHANNEL, r.channel_key, dbLabel(SENTINEL, r.channel_name, r.channel_name))}</td>
                    <td className="num">{num(r.cycles)}</td>
                    <td className="num">{num(r.won)}</td>
                    <td className="num">{r.win_pct === null ? "—" : pctValue(r.win_pct)}</td>
                    <td className="num">{money(r.revenue_aed_minor, "AED")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        <Card title={t(TX.campaignsOnFirstTouch)} hint={t(TX.measuredInferredApart)}>
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={camp} col="campaign">{t(UI.campaign)}</Th>
                  <Th sort={camp} col="source">{t(TX.colHowWeKnow)}</Th>
                  <Th sort={camp} col="cycles" num>{t(TX.colCycles)}</Th>
                  <Th sort={camp} col="won" num>{t(TX.colBanquets)}</Th>
                  <Th sort={camp} col="revenue" num>{t(TX.colRevenueAed)}</Th>
                </tr>
              </thead>
              <tbody>
                {report.topCampaigns.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ color: "var(--ink-3)" }}>
                      {t(TX.noKnownCampaign)}
                    </td>
                  </tr>
                )}
                {camp.rows.map((r, i) => (
                  <tr key={`${r.campaign_name}-${i}`}>
                    <td>{r.campaign_name}</td>
                    <td>
                      <span className={`ref-badge ${r.source === "utm" ? "exact" : "estimate"}`}>
                        {r.source === "utm" ? t(TX.badgeMeasured) : t(TX.badgeInferred)}
                      </span>
                    </td>
                    <td className="num">{num(r.cycles)}</td>
                    <td className="num">{num(r.won)}</td>
                    <td className="num">{money(r.revenue_aed_minor, "AED")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="note" style={{ marginTop: 12 }}>
            <b>{t(TX.noteMeasured)}</b> {t(TX.measuredMeans)} <b>{t(TX.noteInferred)}</b>{" "}
            {t(TX.inferredMeans)}
          </div>
        </Card>

        <Card title={t(TX.arrivedOneWay)} hint={t(TX.divergencesOnly)}>
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={path} col="first">{t(TX.colFirstTouch)}</Th>
                  <Th sort={path} col="last">{t(TX.colLastTouch)}</Th>
                  <Th sort={path} col="cycles" num>{t(TX.colCycles)}</Th>
                  <Th sort={path} col="won" num>{t(TX.colBanquets)}</Th>
                </tr>
              </thead>
              <tbody>
                {report.conversionPath.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ color: "var(--ink-3)" }}>
                      {t(TX.noDivergence)}
                    </td>
                  </tr>
                )}
                {path.rows.map((r, i) => (
                  <tr key={i}>
                    <td>{dbLabel(CHANNEL, r.first_channel_key, dbLabel(SENTINEL, r.first_channel, r.first_channel))}</td>
                    <td>{dbLabel(CHANNEL, r.last_channel_key, dbLabel(SENTINEL, r.last_channel, r.last_channel))}</td>
                    <td className="num">{num(r.cycles)}</td>
                    <td className="num">{num(r.won)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {report.priorPaid !== undefined && <PriorPaidCard block={report.priorPaid} />}

      <Card title={t(TX.whatChatsCouldTell)} hint={t(TX.limitsStated)}>
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <Th sort={cov} col="outcome">{t(TX.colOutcome)}</Th>
                <Th sort={cov} col="leads" num>{t(UI.leads)}</Th>
                <Th sort={cov} col="share" num>{t(UI.share)}</Th>
              </tr>
            </thead>
            <tbody>
              {cov.rows.map((r) => (
                <tr key={r.confidence}>
                  <td>{dbLabel(COVERAGE_LABEL, r.confidence, r.confidence)}</td>
                  <td className="num">{num(r.leads)}</td>
                  <td className="num">{pct(r.leads, coverageTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="note" style={{ marginTop: 12 }}>
          {t(TX.identifiedBy)} <b>{t(TX.byPhoneNumber)}</b>
          {t(TX.identityStitching)}
          <code>ctwa_clid</code>
          {t(TX.ctwaUnavailable)}
        </div>
      </Card>
    </>
  );
}

/**
 * «Этот человек ранее обращался с рекламы».
 *
 * ОТДЕЛЬНЫЙ КОМПОНЕНТ, потому что у него свои два хука, и после падения экрана
 * в пустой синий 10.09.2026 хуки в этом файле больше не добавляются к чужому
 * телу функции: `useState` переключателя и `useSortable` таблицы стоят здесь на
 * верхнем уровне, до единственного возврата.
 *
 * ЧИСЛО НА ПЛИТКЕ И СПИСОК ПОД НИМ СОВПАДАЮТ ПО УМОЛЧАНИЮ. Представление
 * отдаёт и случаи за порогом разрыва — их видно, но по умолчанию таблица
 * показывает ровно то, что на плитке, иначе под числом 63 стояло бы 99 строк.
 * Случаи за окном не спрятаны, а убраны за переключатель, который называет их
 * число: спрятать их значило бы спрятать от проверки то, из-за чего порог и
 * понадобился.
 */
function PriorPaidCard({ block }: { block: PriorPaidBlock }): React.ReactElement {
  const [showBeyond, setShowBeyond] = useState(false);
  const visible = showBeyond ? block.cases : block.cases.filter((c) => c.within_window);
  /* Таблица ПОЛНАЯ — потолок сервера 200 строк, а по всей базе таких случаев
     99, — поэтому сортировать её можно честно, без пометки `partial`. */
  const det = useSortable(visible, {
    lead: {
      kind: "text",
      get: (r) => (r.lead_title === null || r.lead_title === "" ? `#${r.lead_id}` : r.lead_title),
    },
    /* Дата приходит строкой `YYYY-MM-DD`: текстовая колонка упорядочивает её
       верно, сегменты выровнены по позициям. */
    enquiredOn: { kind: "text", get: (r) => r.enquiry_on },
    priorOn: { kind: "text", get: (r) => r.prior_on },
    platform: { kind: "text", get: (r) => r.prior_platform },
    campaign: { kind: "text", get: (r) => r.prior_campaign_name },
    basis: { kind: "text", get: (r) => dbLabel(PRIOR_BASIS_LABEL, r.prior_basis, r.prior_basis) },
    gap: { kind: "number", get: (r) => r.gap_days },
    won: { kind: "number", get: (r) => (r.won ? 1 : 0) },
  });

  const k = block.kpi;
  const cases = k?.cases ?? 0;
  const beyond = k?.cases_beyond_window ?? 0;
  const windowDays = k?.window_days ?? 0;
  const gapTop = block.gapBuckets.reduce((n, b) => (b.cases > n ? b.cases : n), 0);

  return (
    <>
      <Card title={t(TX.priorPaidTitle)} hint={t(TX.priorPaidHint)}>
        <div className="grid cols-4" style={{ marginBottom: 14 }}>
          <Kpi label={t(TX.priorPaidKpi)} value={num(cases)} foot={t(TX.priorPaidKpiFoot)} />
          <Kpi
            label={t(TX.priorPaidGapKpi)}
            value={k?.median_gap_days === null || k === null ? "—" : num(k.median_gap_days)}
            foot={`${t(TX.priorPaidGapFoot)} ${num(windowDays)} ${t(TX.priorPaidDays)}`}
          />
          <Kpi
            label={t(TX.priorPaidBeyondKpi)}
            value={num(beyond)}
            foot={t(TX.priorPaidBeyondFoot)}
          />
          <Kpi
            label={t(TX.priorPaidWonKpi)}
            value={num(block.wonDeals)}
            foot={`${t(TX.priorPaidWonFoot)} ${money(block.wonRevenueAedMinor, "AED")}`}
          />
        </div>

        {/* Главное отрицание стоит В БЛОКЕ и первым, а не сноской под таблицей. */}
        <div className="note">
          <b>{t(TX.priorPaidNotAttributionLead)}</b> {t(TX.priorPaidNotAttribution)}{" "}
          {t(TX.priorPaidNoImpressions)}
        </div>
        <div className="note" style={{ marginTop: 10 }}>
          {t(TX.priorPaidStitching)}
        </div>
      </Card>

      <div className="grid cols-2" style={{ marginTop: 14 }}>
        <Card title={t(TX.priorPaidGapSpread)} hint={t(TX.priorPaidGapSpreadHint)}>
          {block.gapBuckets.map((b) => (
            <div className="funnel-row" key={b.bucket_order}>
              <span>{dbLabel(GAP_BUCKET_LABEL, b.bucket_key, b.bucket_key)}</span>
              <div className="bar">
                <i style={{ width: `${gapTop > 0 ? (100 * b.cases) / gapTop : 0}%` }} />
              </div>
              <span className="num">{num(b.cases)}</span>
            </div>
          ))}
          <div className="note" style={{ marginTop: 12 }}>{t(TX.priorPaidGapNote)}</div>
        </Card>

        <Card title={t(TX.priorPaidCases)} hint={t(TX.priorPaidCasesHint)}>
          {beyond > 0 && (
            <button
              type="button"
              className="btn-ghost"
              style={{ marginBottom: 10 }}
              onClick={() => setShowBeyond(!showBeyond)}
            >
              {showBeyond
                ? t(TX.priorPaidHideBeyond)
                : `${t(TX.priorPaidShowBeyond)} (${num(beyond)})`}
            </button>
          )}
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={det} col="lead">{t(TX.colEnquiry)}</Th>
                  <Th sort={det} col="enquiredOn">{t(TX.colEnquiredOn)}</Th>
                  <Th sort={det} col="priorOn">{t(TX.colPriorOn)}</Th>
                  <Th sort={det} col="platform">{t(TX.colPlatform)}</Th>
                  <Th sort={det} col="campaign">{t(TX.colPriorCampaign)}</Th>
                  <Th sort={det} col="basis">{t(TX.colPriorBasis)}</Th>
                  <Th sort={det} col="gap" num>{t(TX.colGapDays)}</Th>
                  <Th sort={det} col="won">{t(TX.colBanquet)}</Th>
                </tr>
              </thead>
              <tbody>
                {det.rows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ color: "var(--ink-3)" }}>
                      {t(TX.priorPaidNoCases)}
                    </td>
                  </tr>
                )}
                {det.rows.map((r) => (
                  <tr key={r.lead_id}>
                    <td>
                      {r.lead_title === null || r.lead_title === "" ? `#${r.lead_id}` : r.lead_title}
                    </td>
                    <td>{r.enquiry_on}</td>
                    <td>{r.prior_on}</td>
                    {/* Названия платформ и кампаний не переводятся: они совпадают
                        с кабинетом, откуда пришли. */}
                    <td>{r.prior_platform ?? "—"}</td>
                    <td>
                      {r.prior_campaign_name ?? (
                        <span style={{ color: "var(--ink-3)" }}>{t(TX.priorCampaignUnknown)}</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={r.prior_basis === "client_tag" ? "tag" : "tag tag-warn"}
                        title={r.prior_basis === "client_tag" ? undefined : t(TX.badgeInferred)}
                      >
                        {dbLabel(PRIOR_BASIS_LABEL, r.prior_basis, r.prior_basis)}
                      </span>
                    </td>
                    <td className="num">
                      {num(r.gap_days)}
                      {!r.within_window && (
                        <>
                          {" "}
                          <small style={{ color: "var(--ink-3)" }}>{t(TX.beyondWindowRow)}</small>
                        </>
                      )}
                    </td>
                    <td>{r.won ? money(r.revenue_aed_minor, "AED") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}
